import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { apiUrl } from "../config/apiBase";
import { maAdminPristup } from "./pristupyPouzivatelov";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const r = await fetch(apiUrl("/api/auth/me"), { credentials: "include" });
    if (!r.ok) throw new Error("Nepodarilo sa načítať používateľa");
    const data = await r.json();
    setUser(data.user || null);
    return data.user || null;
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const r = await fetch(apiUrl("/api/auth/me"), { credentials: "include" });

        if (r.ok) {
          const { user } = await r.json();
          if (!cancelled) setUser(user || null);
        } else {
          if (!cancelled) setUser(null);
        }
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    let r;

    try {
      r = await fetch(apiUrl("/api/auth/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password })
      });
    } catch {
      throw new Error(
        "Server je momentálne nedostupný. Skúste to prosím znova."
      );
    }

    if (!r.ok) {
      let msg =
        r.status >= 500
          ? "Server je momentálne nedostupný. Skúste to prosím znova."
          : "Nesprávne prihlasovacie údaje.";

      try {
        const data = await r.json();

        if (data && typeof data.message === "string") {
          msg = data.message;
        }
      } catch {}

      throw new Error(msg);
    }

    const data = await r.json();

    if (data?.onboarding) {
      setUser(data.user || null);
      return {
        onboarding: true,
        user: data.user || null
      };
    }

    setUser(data.user || null);
    return data.user || null;
  }, []);

  const dokoncitPrveSpustenie = useCallback(async (password, password2) => {
    const r = await fetch(apiUrl("/api/auth/prve-spustenie/dokonci"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ password, password2 })
    });

    if (!r.ok) {
      let msg = "Nepodarilo sa dokončiť prvé spustenie.";

      try {
        const data = await r.json();
        if (data?.message) msg = data.message;
      } catch {}

      if (r.status === 401) {
        setUser(null);
        throw new Error("Onboarding relácia vypršala. Prihlás sa znova dočasným heslom.");
      }

      throw new Error(msg);
    }

    const { user } = await r.json();
    setUser(user || null);
    return user || null;
  }, []);

  const nacitajProfil = useCallback(async () => {
    const r = await fetch(apiUrl("/api/profil"), {
      credentials: "include"
    });

    if (!r.ok) {
      let msg = "Nepodarilo sa načítať profil.";
      try {
        const data = await r.json();
        if (data?.message) msg = data.message;
      } catch {}
      throw new Error(msg);
    }

    const { user } = await r.json();
    setUser(user || null);
    return user || null;
  }, []);

  const ulozProfil = useCallback(async ({ name, priezvisko, telefon, nbsCislo }) => {
    const r = await fetch(apiUrl("/api/profil"), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name, priezvisko, telefon, nbsCislo })
    });

    if (!r.ok) {
      let msg = "Nepodarilo sa uložiť profil.";
      try {
        const data = await r.json();
        if (data?.message) msg = data.message;
      } catch {}
      throw new Error(msg);
    }

    const { user } = await r.json();
    setUser(user || null);
    return user || null;
  }, []);

  const zmenHeslo = useCallback(async ({ stareHeslo, noveHeslo, noveHeslo2 }) => {
    const r = await fetch(apiUrl("/api/profil/zmen-heslo"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ stareHeslo, noveHeslo, noveHeslo2 })
    });

    if (!r.ok) {
      let msg = "Nepodarilo sa zmeniť heslo.";
      try {
        const data = await r.json();
        if (data?.message) msg = data.message;
      } catch {}
      throw new Error(msg);
    }

    return r.json();
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(apiUrl("/api/auth/logout"), {
        method: "POST",
        credentials: "include"
      });
    } finally {
      setUser(null);
    }
  }, []);

  const value = {
    user,
    loading,
    isAdmin: maAdminPristup(user),
    login,
    logout,
    refreshUser,
    dokoncitPrveSpustenie,
    nacitajProfil,
    ulozProfil,
    zmenHeslo
  };

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}